<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" :id="testName + '-title'">
        {{ testName }}
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="variant-detection-test-grid"
      >
        <div>
          <p>
            Testing of individuals for one or more
            <a
              href="https://www.vcgs.org.au/tests/cascade-family-testing/"
              target="_blank"
            >
              mitochondrial genome variants previously identified in a family
              member</a
            >
            is available. This includes predictive testing (asymptomatic
            individuals), parental or familial segregation, and carrier testing.
          </p>
        </div>
        <v-row>
          <v-col class="custom-col">
            <v-checkbox
              v-model="previouslyTested"
              item-text="key"
              item-value="value"
              label="Family member previously tested at VCGS"
              class="mb-3"
              :id="testId + '.familyPrevTested'"
            >
            </v-checkbox>
          </v-col>
        </v-row>
        <div v-if="previouslyTested === true">
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">VCGS sample ID</span>

              <v-text-field
                v-if="previouslyTested === true"
                v-model="posCtrlSampleId"
                :id="testId + '.posCtrlSampleId'"
                outlined
                hide-details="auto"
              ></v-text-field>
              <span class="text-hint"
                >e.g. 20C100123. This is located in the top right section of our
                reports.</span
              >
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family member name</span>
              <v-text-field
                v-model="posCtrlName"
                :id="testId + '.posCtrlName'"
                outlined
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family member date of birth</span>
              <v-menu
                v-model="familyMemberDOBMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="familyMemberDOBDisplay"
                    :id="testId + '.posCtrlBirthDate'"
                    v-on="on"
                    :rules="familyMemberDOBDisplay != '' ? ruleDate : []"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </template>
                <v-date-picker
                  locale="en-au"
                  :min="minDate"
                  :max="maxDate"
                  v-model="familyDOBVal"
                  @input="familyMemberDOBMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </div>

        <div
          class="mb-3 mt-3 mb-md-6 mt-md-8"
          v-if="
            this.$dataset.isUploadPreviousReportEnabled &&
              previouslyTested === false
          "
        >
          <UploadComponent
            :testId="testId"
            :testName="testName"
            :updateOnDestroy="true"
            @updateUploadObj="updateUploadObj"
            uploadType="previousReport"
          />
        </div>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title mt-4"
              >Relationship of this patient to positive control*</span
            >
            <v-text-field
              :rules="ruleNotNull"
              v-model="rhipToPosCtrl"
              :id="testId + '.rhipToPosCtrl'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint">e.g. sibling, parent, aunt, etc.</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title"
              >Is the person being tested affected by the same condition?</span
            >
            <v-text-field
              v-model="relationAffected"
              :id="testId + '.relationAffected'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="e.g. mildly affected"></span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Number of SNVs to be tested*</span>
            <v-select
              v-model="numVariants"
              :items="numVariantsRange"
              :id="testId + '.numVariants'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-alert v-if="numVariants" text color="info" outlined>
          If not all the variants on the report are being tested, please specify
          the subset of variants in the clinical notes section.
        </v-alert>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Clinical notes</span>
            <v-textarea
              v-model="clinicalNotes"
              :id="testId + '.clinicalNotes'"
              outlined
              hide-details="auto"
            ></v-textarea>
            <span class="text-hint"
              >e.g. details of familial variant(s), clinical indication for
              testing, results of relevant prior genetic and non-genetic
              investigations, biochemistry, histopathology and imaging,
              etc.</span
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Family history</span>
            <v-textarea
              v-model="familyHx"
              :id="testId + '.familyHx'"
              outlined
              hide-details="auto"
            ></v-textarea>
            <span class="text-hint"
              >e.g. affected status of the parents, parental consanguinity,
              etc.</span
            >
          </v-col>
        </v-row>
        <TestPriceAlert :price="price" :medicare="medicare" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="familial_varient_next_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import DropDownMixin from "../../mixins/DropDownMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert.vue";
import UploadComponent from "../upload/UploadComponent.vue";
import UploadMixin from "../../mixins/UploadMixin.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin, UploadMixin, RetrieveJSONMixin],
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  components: { TestPriceAlert, UploadComponent },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyDOBVal: function() {
      this.familyMemberDOBDisplay = this.familyDOBVal
        ? this.formatDate(this.familyDOBVal)
        : "";
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.pricing = await this.execRetrieveJson(
      "json/pricing/whole_mito_cascade_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById("mito-cascade-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  data: () => ({
    pricing: null,
    previouslyTested: false,
    relationAffected: "",
    rhipToPosCtrl: "",
    familyHx: "",
    posCtrlSampleId: "",
    clinicalNotes: "",
    numVariants: "",
    numVariantsRange: [1, 2, 3, 4],
    posCtrlName: "",
    familyMemberDOBMenu: false,
    familyDOBVal: null,
    familyMemberDOBDisplay: "",
    minDate: "1801-01-01",
    maxDate: new Date().toJSON().slice(0, 10),
  }),
  computed: {
    getPriceDetails() {
      if (!this.pricing) return null;
      let internallyTestedKey = this.previouslyTested
        ? "internallyDetected"
        : "externallyDetected";
      return this.pricing[internallyTestedKey];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails && this.numVariants) {
        return priceDetails.price * parseFloat(this.numVariants);
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },
  methods: {
    formatDate: function(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    reverseFormatDate: function(date) {
      if (!date) return null;
      const [day, month, year] = date.split("/");
      return `${year}-${month}-${day}`;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      return {
        testType: this.testId,
        familyPrevTested: this.previouslyTested,
        posCtrlSampleId: this.posCtrlSampleId,
        relationAffected: this.relationAffected,
        familyHx: this.familyHx,
        numVariants: this.numVariants,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        rhipToPosCtrl: this.rhipToPosCtrl,
        posCtrlName: this.posCtrlName,
        posCtrlBirthDate: this.reverseFormatDate(this.familyMemberDOBDisplay),
      };
    },
  },
};
</script>
